import React from "react"
import PropTypes from "prop-types"
import cn from "classnames"
import { propsUtil } from "@asphalt-react/helper"

import styles from "./heading.module.css"
import intentStyles from "./intent.module.css"
import { resolveIntent } from "./props.js"

const { resolvePropCollision } = propsUtil

export const Heading = ({
  children,
  h1 = false,
  h2 = false,
  h3 = false,
  h4 = false,
  h5 = false,
  h6 = false,
  primary = false,
  secondary = false,
  brand = false,
  onBrand = false,
  muted = false,
  ...props
}) => {
  /* eslint-disable-next-line react/prop-types */
  const { style, className, ...rest } = props

  const { collision, value: Tag } = resolvePropCollision(
    {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
    },
    "h1"
  )

  if (collision) {
    console.warn(`Heading: Prop collision detected, falling back to "${Tag}".`)
  }

  const { collision: intentCollision, value: intent } = resolveIntent({
    primary,
    secondary,
    brand,
    onBrand,
    muted,
  })

  if (intentCollision) {
    console.warn(
      `Heading: Multiple intents detected, falling back to "${intent}" intent.`
    )
  }

  const classes = cn(styles.Heading, styles[Tag], intentStyles[intent])

  return (
    <Tag {...rest} className={classes}>
      {children}
    </Tag>
  )
}

Heading.displayName = "Heading"

Heading.propTypes = {
  /**
   *
   * React node for heading content.
   */
  children: PropTypes.node.isRequired,
  /**
   * Render the content as h1 section heading.
   */
  h1: PropTypes.bool,
  /**
   * Render the content as h2 section heading.
   */
  h2: PropTypes.bool,
  /**
   * Render the content as h3 section heading.
   */
  h3: PropTypes.bool,
  /**
   * Render the content as h4 section heading.
   */
  h4: PropTypes.bool,
  /**
   * Render the content as h5 section heading.
   */
  h5: PropTypes.bool,
  /**
   * Render the content as h6 section heading.
   */
  h6: PropTypes.bool,
  /**
   * Renders the text with primary intent. This is the default intent.
   */
  primary: PropTypes.bool,
  /**
   * Renders the text with secondary intent.
   */
  secondary: PropTypes.bool,
  /**
   * Renders the text with brand intent.
   */
  brand: PropTypes.bool,
  /**
   * Renders the text with appropriate contrast ration to backgrounds with brand color.
   */
  onBrand: PropTypes.bool,
  /**
   * Renders the text with muted intent.
   */
  muted: PropTypes.bool,
}

Heading.defaultProps = {
  h1: false,
  h2: false,
  h3: false,
  h4: false,
  h5: false,
  h6: false,
  primary: false,
  secondary: false,
  brand: false,
  onBrand: false,
  muted: false,
}
